import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// PrimeNG
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { Ticket } from '../ticket-management.component';
import { ApiService } from '../../../../services/api.service';

export interface Comment {
  id?: number;
  userId: string;
  userName: string;
  comment: string;
  createdAt: Date;
  ticketId?: number;
}

@Component({
  selector: 'app-comment-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    ToastModule
  ],
  templateUrl: './comment-modal.component.html',
  styleUrls: ['./comment-modal.component.css']
})
export class CommentModalComponent implements OnInit, OnChanges {

  @Input() visible: boolean = false;
  @Input() ticket: Ticket | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() commentAdded = new EventEmitter<Comment>();

  commentText: string = '';
  comments: Comment[] = [];

  currentUser: string = 'CURRENT_USER'; // replace with auth user

  constructor(
    private api: ApiService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    if (this.ticket) {
      this.loadComments();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['visible'] && this.visible && this.ticket) {
      this.loadComments();
      this.commentText = '';
    }

  }

  /* ---------------------------------------
        LOAD COMMENTS API
  ----------------------------------------*/

 loadComments(): void {

  if (!this.ticket?.id) return;

  const url = `/ticket/${this.ticket.id}/comments/`;

  this.api.getAPI(url).subscribe({

    next: (res: any) => {

      console.log("COMMENTS RESPONSE:", res);

      if (res.status === 200) {

        this.comments = res.data.map((item: any) => ({

          id: item.id,

          userId: item.user,

          userName: item.user_info?.loginname || 'User',

          comment: item.comment,

          createdAt: new Date(item.created_on),

          ticketId: item.ticket

        }));

      }

    },

    error: (err: any) => {

      console.error("Error loading comments", err);

    }

  });

}

  /* ---------------------------------------
        ADD COMMENT
  ----------------------------------------*/

  onSubmitComment(): void {

    if (!this.commentText.trim()) {

      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please enter a comment'
      });

      return;
    }

    const payload = {
      // ticket: this.ticket?.id,
      comment: this.commentText
    };

    this.api.postAPI(`/ticket/${this.ticket.id}/comments/`, payload).subscribe({

      next: (res: any) => {

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Comment added successfully'
        });

        this.commentText = '';

        this.loadComments();   // ✅ refresh comments

      },

      error: (err: any) => {

        console.error(err);

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add comment'
        });

      }

    });

  }

  /* ---------------------------------------
        DATE FORMAT
  ----------------------------------------*/

  formatDate(date: Date): string {

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);

  }

  getRelativeTime(date: Date): string {

    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;

    return this.formatDate(date);
  }

  /* ---------------------------------------
        CLOSE MODAL
  ----------------------------------------*/

  onClose(): void {
    this.commentText = '';
    this.close.emit();
  }

  onHide(): void {
    this.commentText = '';
    this.close.emit();
  }

}